import React from 'react';
import { TrafficCone, ZoomIn, ZoomOut } from 'lucide-react';

interface ColorPickerProps {
  currentColor?: string;
  workInProgress?: boolean;
  borderRadius?: number;
  iframeZoom?: number;
  showColorPicker?: boolean;
  showBorderRadius?: boolean;
  showIframeZoom?: boolean;
  onColorSelect: (color: string) => void;
  onWorkInProgressToggle: () => void;
  onBorderRadiusChange: (radius: number) => void;
  onIframeZoomChange?: (zoom: number) => void;
}

const COLORS = [
  '#4380F5',
  '#838732',
  '#F9D75B',
  '#F29009',
  '#F7BDB2',
  '#1C2E22',
  '#E9E3E3',
  '#FFFFFF'
];

export const ColorPicker: React.FC<ColorPickerProps> = ({
  currentColor,
  workInProgress,
  borderRadius = 8,
  iframeZoom = 1,
  showColorPicker = true,
  showBorderRadius = true,
  showIframeZoom = false,
  onColorSelect,
  onWorkInProgressToggle,
  onBorderRadiusChange,
  onIframeZoomChange
}) => {
  return (
    <div className="color-picker bg-white border border-gray-300 rounded shadow-lg" style={{ padding: '8px' }}>
      {showColorPicker && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 32px)', gap: '8px', marginBottom: '8px' }}>
          {COLORS.map((color) => (
            <button
              key={color}
              onClick={() => onColorSelect(color)}
              className={`rounded border ${
                currentColor === color ? 'border-blue-500 border-2' : 'border-gray-300'
              } hover:border-gray-400 transition-colors`}
              style={{ width: '32px', height: '32px', backgroundColor: color }}
              title={color}
            />
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: '8px', paddingTop: showColorPicker ? '8px' : '0', borderTop: showColorPicker ? '1px solid #E5E7EB' : 'none' }}>
        {showBorderRadius && (
          <button
            onClick={() => {
              const radiusValues = [4, 8, 16];
              const currentIndex = radiusValues.indexOf(borderRadius);
              const nextIndex = (currentIndex + 1) % radiusValues.length;
              onBorderRadiusChange(radiusValues[nextIndex]);
            }}
            className="flex items-center justify-center border rounded hover:border-gray-400 transition-colors border-gray-300"
            style={{ width: '32px', height: '32px' }}
            title={`Corner radius: ${borderRadius}px (click to cycle)`}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              {borderRadius === 16 && (
                <>
                  <path d="M21 11C21 8.87827 20.1571 6.84344 18.6569 5.34315C17.1566 3.84285 15.1217 3 13 3" stroke="#4B5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="#4B5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </>
              )}
              {borderRadius === 8 && (
                <>
                  <path d="M21 11C21 9.89704 21 7.77893 21 5.99917C21 4.34232 19.6577 3 18.0008 3C16.2211 3 14.103 3 13 3" stroke="#4B5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="#4B5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </>
              )}
              {borderRadius === 4 && (
                <>
                  <path d="M21 11C21 9.42271 21 5.7694 21 3.99837C21 3.44608 20.5539 3 20.0016 3C18.2306 3 14.5773 3 13 3" stroke="#4B5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 15V19C21 19.5304 21 21 21 21C21 21 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3 3 3 3C3 3 4.46957 3 5 3H9" stroke="#4B5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </>
              )}
            </svg>
          </button>
        )}
        {showIframeZoom && onIframeZoomChange && (
          <>
            <button
              onClick={() => {
                const newZoom = Math.max(0.5, iframeZoom - 0.1);
                onIframeZoomChange(Math.round(newZoom * 10) / 10);
              }}
              className="flex items-center justify-center border rounded hover:border-gray-400 transition-colors border-gray-300"
              style={{ width: '32px', height: '32px' }}
              title="Zoom out"
            >
              <ZoomOut size={18} className="text-gray-600" />
            </button>
            <button
              onClick={() => {
                const newZoom = Math.min(2, iframeZoom + 0.1);
                onIframeZoomChange(Math.round(newZoom * 10) / 10);
              }}
              className="flex items-center justify-center border rounded hover:border-gray-400 transition-colors border-gray-300"
              style={{ width: '32px', height: '32px' }}
              title="Zoom in"
            >
              <ZoomIn size={18} className="text-gray-600" />
            </button>
          </>
        )}
        <button
          onClick={onWorkInProgressToggle}
          className={`flex items-center justify-center border rounded hover:border-gray-400 transition-colors ${
            workInProgress ? 'border-blue-500 border-2 bg-blue-50' : 'border-gray-300'
          }`}
          style={{ width: '32px', height: '32px' }}
          title="Toggle work in progress banner"
        >
          <TrafficCone size={18} className={workInProgress ? 'text-blue-600' : 'text-gray-600'} />
        </button>
      </div>
    </div>
  );
};
